import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useMunicipalityId } from '../../hooks/useCouncilPath';
import { getApiClient } from '../../api';
import type { PartiesUnity as PartiesUnityData, DropdownOptions } from '../../api';
import LoadingPage from '../../components/LoadingPage';
import { useTranslation } from 'react-i18next';
import UnityLineChart from '../../components/UnityLineChart';
import PartyBarChart from '../../components/charts/PartyBarChart';
import { redirectErrorPage } from '../../utils/errors';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import { useFormatters } from '../../utils/texts';

function PartiesUnity() {
    const [data, setData] = useState<PartiesUnityData | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [options, setOptions] = useState<DropdownOptions[] | undefined>(undefined);
    const { bodyType, zastupitelstvoId } = useParams();
    const municipalitaId = useMunicipalityId();
    const { t, i18n } = useTranslation('analyses');
    const formatter = useFormatters(i18n.language);

    const averaged = useMemo(
        () =>
            (data?.datasets ?? [])
                .map((dataset) => {
                    const values = dataset.data.filter((item): item is number => typeof item === 'number');
                    const avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
                    return { label: dataset.label, value: Number(avg.toFixed(1)), color: dataset.backgroundColor[0] };
                })
                .sort((a, b) => b.value - a.value),
        [data?.datasets]
    );

    useEffect(() => {
        const fetchData = async () => {
            try {
                const api = getApiClient();
                const councilId = parseInt(zastupitelstvoId ?? '0', 10);
                const [unityData, subjectOptions] = await Promise.all([
                    api.getPartiesUnityByMunicipalityIdAndBodyAndCouncilId(municipalitaId ?? '', bodyType ?? '', councilId),
                    api.getDropdownSubjectOptionsByMunicipalityIdAndBodyAndCouncilId(municipalitaId ?? '', bodyType ?? '', councilId)
                ]);

                setData(unityData);
                setOptions(subjectOptions);
            } catch (error: any) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [municipalitaId, bodyType, zastupitelstvoId]);

    if (loading) {
        return <LoadingPage />;
    }

    if (error) {
        redirectErrorPage(error);
        return null;
    }
    if (data !== undefined && options !== undefined) {
        return (
            <>
                <ContentSection title={t('unity.partiesVoting')} level={1} subtitle={t('unity.partiesVotingTooltip')} className='page-title-section' />
                <ContentSection
                    level={3}
                    title={t('unity.partiesVotingMeetings')}
                    subtitle={t('unity.partiesVotingMeetingsTooltip')}
                    className='dashboard-card'
                >
                    <UnityLineChart data={data} options={options} />
                </ContentSection>
                <ContentSection
                    level={3}
                    title={t('unity.partiesVotingAvg')}
                    subtitle={t('unity.partiesVotingAvgTooltip')}
                    className='dashboard-card'
                >
                    <PartyBarChart
                        items={averaged}
                        height='450px'
                        axisFormat={(value) => formatter.percentFrom100(value)}
                        axisTitle={t('unity.partiesVotingAvg')}
                    />
                </ContentSection>
            </>
        );
    } else {
        return <LoadingPage />;
    }
}

export default PartiesUnity;
